
import React, { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { Loader2, Copy, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface ResetPasswordDialogProps {
  userToReset: {
    id: string;
    email: string;
    name: string;
  } | null;
  onClose: () => void;
  onReset: () => void;
}

export const ResetPasswordDialog: React.FC<ResetPasswordDialogProps> = ({
  userToReset,
  onClose,
  onReset,
}) => {
  const [isSettingPassword, setIsSettingPassword] = useState(false);
  const [newPassword, setNewPassword] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const handleSetPassword = async () => {
    if (!userToReset?.id) {
      toast.error("No user ID provided");
      onClose();
      return;
    }

    setIsSettingPassword(true);
    try {
      // Use the setPassword action in the edge function
      const { data, error } = await supabase.functions.invoke('invite-user', {
        body: {
          action: 'setPassword',
          userId: userToReset.id
        }
      });
      
      if (error || (data && data.error)) {
        console.error("Edge function error:", error || data?.error);
        throw new Error(error?.message || data?.error || "Failed to set password");
      }
      
      setNewPassword(data.newPassword);
      toast.success(`New password set for ${userToReset.name || userToReset.email}`);
      onReset();
    } catch (error: any) {
      console.error("Error setting password:", error);
      toast.error(`Failed to set password: ${error.message}`);
    } finally {
      setIsSettingPassword(false);
    }
  };

  const copyPassword = async () => {
    if (newPassword) {
      await navigator.clipboard.writeText(newPassword);
      setCopied(true);
      toast.success("Password copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleClose = () => {
    setNewPassword(null);
    setCopied(false);
    onClose();
  };

  return (
    <AlertDialog open={!!userToReset} onOpenChange={handleClose}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Set New Password</AlertDialogTitle>
          <AlertDialogDescription>
            {!newPassword ? (
              <>
                Are you sure you want to set a new password for {userToReset?.name || userToReset?.email}?
                <p className="mt-2">
                  A new random password will be generated and displayed for you to share with the user.
                </p>
              </>
            ) : (
              <>
                New password has been set for {userToReset?.name || userToReset?.email}.
                <div className="mt-4 p-3 bg-muted rounded-lg">
                  <p className="text-sm font-medium mb-2">New Password:</p>
                  <div className="flex items-center gap-2">
                    <code className="bg-background px-2 py-1 rounded text-sm font-mono">
                      {newPassword}
                    </code>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={copyPassword}
                      className="h-8 w-8 p-0"
                    >
                      {copied ? (
                        <CheckCircle className="h-4 w-4 text-green-600" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    Please share this password securely with the user.
                  </p>
                </div>
              </>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isSettingPassword}>
            {newPassword ? 'Close' : 'Cancel'}
          </AlertDialogCancel>
          {!newPassword && (
            <AlertDialogAction 
              onClick={(e) => {
                e.preventDefault();
                handleSetPassword();
              }}
              disabled={isSettingPassword}
            >
              {isSettingPassword ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Setting Password...
                </>
              ) : (
                'Set New Password'
              )}
            </AlertDialogAction>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
